// eslint-disable-next-line no-duplicate-imports
import * as React from 'react';
import { ErrorInfo, Suspense, useState } from 'react';
import { cn } from '@/lib/utils';
import type { CoreLayoutProps } from 'ra-core';
import { ErrorBoundary } from 'react-error-boundary';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Notification } from '@/components/admin/notification';
import { AppSidebar } from '@/components/admin/app-sidebar';
import { Error } from '@/components/admin/error';
import { Loading } from '@/components/admin/loading';
import { DevToolbar, shouldShowDevToolbar } from '../dev/DevToolbar';
import ScrollToTop from '@/components/ScrollToTop.tsx';

/**
 * The main application layout with sidebar, header, and content area.
 *
 * Renders the app structure with a collapsible sidebar (theme toggle + user
 * menu in its footer), a header with breadcrumb navigation, and the main
 * content area. Includes error boundary and loading states.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/layout/ Layout documentation}
 */
export const Layout = (props: CoreLayoutProps) => {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | undefined>(undefined);
  const handleError = (_: unknown, info: ErrorInfo) => {
    setErrorInfo(info);
  };
  return (
    // Backdrop: fills the viewport with the design canvas (dot grid + ambient
    // glow + page background) via `.canvas-bg` — the same treatment the body
    // carries. The `--shell-gap` padding is the gap the floating shell sits in,
    // so the dotted, lit canvas shows as a thin frame on every edge.
    <div className="canvas-bg h-svh w-full overflow-hidden p-[var(--shell-gap)]">
      {/* The shell: sidebar + chrome + content, lifted off the backdrop as one
          rounded `bg-sidebar` panel that fills the padded area. */}
      <SidebarProvider className="chrome-gradient h-full overflow-hidden rounded-3xl border border-sidebar-border border-t-white/15 shadow-[0_10px_30px_-14px_rgba(0,0,0,0.6)] max-w-[1660px] mx-auto">
        {/*
        Skip link: the first focusable element in the DOM so keyboard users can
        jump past the sidebar nav straight to the main content. Visually hidden
        until focused, then pinned to the top-left as a standard affordance.
      */}
        <a
          href="#layout-scroll"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-elev-2 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Skip to main content
        </a>
        <ScrollToTop />
        <AppSidebar />
        <main
          className={cn(
            'ml-auto w-full max-w-full',
            // The chrome frame: header strip, footer strip, and the gaps around
            // the content well all read as one continuous `bg-sidebar` surface,
            // with a viewport-fixed diagonal wash (`chrome-gradient`) that flows
            // unbroken across the whole control element (sidebar included).
            'bg-sidebar text-sidebar-foreground chrome-gradient',
            'peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]',
            'peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]',
            'sm:transition-[width] sm:duration-200 sm:ease-linear',
            // h-full (not h-svh): the main fills the floated shell, which is itself
            // shorter than the viewport by the outer gap.
            'flex h-full flex-col',
            'group-data-[scroll-locked=1]/body:h-full',
            'has-[main.fixed-main]:group-data-[scroll-locked=1]/body:h-svh'
          )}
        >
          {shouldShowDevToolbar() && <DevToolbar />}
          <div className="relative flex flex-1 flex-col min-w-0 overflow-hidden">
            {/* Header strip — in-flow chrome at the top of the frame (NOT a pill).
              Holds the breadcrumb + actions directly on the `bg-sidebar` surface;
              the recessed well sits below it. */}
            <header className="flex items-center justify-between shrink-0 h-[var(--app-header-h)] px-[var(--shell-gap)] text-sm">
              {/* `pl-[--shell-gap]` indents the left header content (breadcrumb
                  on detail pages, tabs on the list) by 12px so it lines up with
                  the card grid. The right `#header-actions` keeps the header's
                  plain 12px edge. */}
              <div
                className="flex items-center min-w-0 flex-1 mr-4 pl-[var(--shell-gap)] overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                id="breadcrumb"
              />
              <div className="flex items-center gap-2 shrink-0">
                <div id="header-actions" className="flex items-center gap-2" />
              </div>
            </header>
            <ErrorBoundary
              onError={handleError}
              fallbackRender={({ error, resetErrorBoundary }) => (
                <Error
                  error={error}
                  errorInfo={errorInfo}
                  resetErrorBoundary={resetErrorBoundary}
                />
              )}
            >
              <Suspense fallback={<Loading />}>
                <div
                  id="layout-scroll"
                  tabIndex={-1}
                  className="canvas-bg flex flex-1 flex-col min-h-0 overflow-y-auto outline-none mx-[var(--shell-gap)] mb-[var(--shell-gap)] rounded-2xl border border-border shadow-[var(--shadow-well)]"
                >
                  {/*
                  PageTransition is NOT applied here anymore. Wrapping the
                  whole routed tree in a keyed AnimatePresence remounted
                  AdvertiserLayout (and its data/context) on every navigation
                  — a visible mount→unmount→remount flash. The transition now
                  lives inside AdvertiserLayout, wrapping only the <Outlet/>,
                  so the layout stays mounted and just the page content
                  animates.
                */}
                  {props.children}
                </div>
              </Suspense>
            </ErrorBoundary>
            {/*
            page-footer slot: an in-flow chrome strip below the well. Footer
            content (portaled in) sits directly on the `bg-sidebar` frame.
            `empty:hidden` collapses the strip to zero height when no footer is
            portaled (e.g. the campaign list), so the well extends to the bottom.
          */}
            <div
              id="page-footer"
              className="shrink-0 empty:hidden -mt-[var(--shell-gap)] h-[var(--app-header-h)] flex flex-col justify-center px-[var(--shell-gap)]"
            />
          </div>
        </main>
        <Notification />
      </SidebarProvider>
    </div>
  );
};
